/**
 * Single Google Maps JS loader for Hang.
 * Loads Maps + Places once; all callers await this instead of injecting scripts.
 * Key: REACT_APP_GOOGLE_MAPS_API_KEY, fallback to existing client key if unset.
 */
const FALLBACK_GOOGLE_MAPS_API_KEY = 'AIzaSyAP-8oI9lGS6a4iXlCdSD7bZDyz_D-FGAs';

export const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  process.env.REACT_APP_GOOGLE_PLACES_API_KEY ||
  FALLBACK_GOOGLE_MAPS_API_KEY;

/** Stable libraries array for @react-google-maps/api useJsApiLoader */
export const GOOGLE_MAPS_LIBRARIES = ['places'];

let loadPromise = null;

export function isGoogleMapsReady() {
  return !!(window.google && window.google.maps && window.google.maps.places);
}

/**
 * Ensure Google Maps JS API (with Places) is loaded exactly once.
 * Safe to call from multiple components concurrently.
 */
export function loadGoogleMaps() {
  if (isGoogleMapsReady()) {
    return Promise.resolve(window.google);
  }

  if (loadPromise) {
    return loadPromise;
  }

  const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
  if (existingScript) {
    loadPromise = new Promise((resolve, reject) => {
      if (isGoogleMapsReady()) {
        resolve(window.google);
        return;
      }
      const onLoad = () => {
        if (isGoogleMapsReady()) {
          resolve(window.google);
        } else {
          // useJsApiLoader may fire load before places attaches — poll briefly
          let tries = 0;
          const poll = setInterval(() => {
            tries += 1;
            if (isGoogleMapsReady()) {
              clearInterval(poll);
              resolve(window.google);
            } else if (tries > 40) {
              clearInterval(poll);
              reject(new Error('Google Maps loaded but Places library missing'));
            }
          }, 50);
        }
      };
      existingScript.addEventListener('load', onLoad);
      existingScript.addEventListener('error', () =>
        reject(new Error('Failed to load Google Maps API'))
      );
      // Script may already be loaded (e.g. by useJsApiLoader)
      if (existingScript.getAttribute('data-loaded') === 'true' || isGoogleMapsReady()) {
        onLoad();
      } else {
        // Also poll in case load already fired before we attached
        let tries = 0;
        const poll = setInterval(() => {
          tries += 1;
          if (isGoogleMapsReady()) {
            clearInterval(poll);
            resolve(window.google);
          } else if (tries > 100) {
            clearInterval(poll);
            // leave promise pending for load event; don't reject yet if script still loading
          }
        }, 50);
      }
    });
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      script.setAttribute('data-loaded', 'true');
      if (isGoogleMapsReady()) {
        resolve(window.google);
      } else {
        reject(new Error('Google Maps loaded but Places library missing'));
      }
    };
    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Failed to load Google Maps API'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}

export default loadGoogleMaps;
